import { API_URL as BASE_URL } from '../../lib/config';
const API_URL = `${BASE_URL}/platform-admin`;

export interface Organization {
  id: string;
  name: string;
  timezone: string;
  billingEmail: string | null;
  status: 'ACTIVE' | 'PAUSED' | 'TRIAL' | 'DELETED' | 'SUSPENDED';
  pausedAt: string | null;
  pausedReason: string | null;
  deletedAt: string | null;
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    users: number;
    agents: number;
  };
}

export interface OrganizationStats {
  total: number;
  active: number;
  paused: number;
  trial: number;
  deleted: number;
}

export interface CreateOrganizationDto {
  name: string;
  timezone: string;
  billingEmail: string;
  status?: 'ACTIVE' | 'TRIAL';
  adminUser: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  };
}

export interface UpdateOrganizationDto {
  name?: string;
  timezone?: string;
  billingEmail?: string;
}

export interface PauseOrganizationDto {
  reason: string;
}

export interface DeleteOrganizationDto {
  reason: string;
}

export interface GetOrganizationsParams {
  status?: string;
  search?: string;
  skip?: number;
  take?: number;
}

export interface PaginatedOrganizations {
  data: Organization[];
  total: number;
  skip: number;
  take: number;
}

export const platformOrganizationsService = {
  async getStats(token: string): Promise<OrganizationStats> {
    const response = await fetch(`${API_URL}/organizations/stats`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch organization stats');
    }

    return response.json();
  },

  async getOrganizations(
    token: string,
    params?: GetOrganizationsParams
  ): Promise<PaginatedOrganizations> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params?.take !== undefined) queryParams.append('take', params.take.toString());

    const url = `${API_URL}/organizations${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch organizations');
    }

    return response.json();
  },

  async getOrganizationById(token: string, id: string): Promise<Organization> {
    const response = await fetch(`${API_URL}/organizations/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch organization');
    }

    return response.json();
  },

  async createOrganization(
    token: string,
    data: CreateOrganizationDto
  ): Promise<Organization> {
    const response = await fetch(`${API_URL}/organizations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create organization');
    }

    return response.json();
  },

  async updateOrganization(
    token: string,
    id: string,
    data: UpdateOrganizationDto
  ): Promise<Organization> {
    const response = await fetch(`${API_URL}/organizations/${id}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update organization');
    }

    return response.json();
  },

  async pauseOrganization(
    token: string,
    id: string,
    data: PauseOrganizationDto
  ): Promise<Organization> {
    const response = await fetch(`${API_URL}/organizations/${id}/pause`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to pause organization');
    }

    return response.json();
  },

  async resumeOrganization(token: string, id: string): Promise<Organization> {
    const response = await fetch(`${API_URL}/organizations/${id}/resume`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to resume organization');
    }

    return response.json();
  },

  async deleteOrganization(
    token: string,
    id: string,
    data: DeleteOrganizationDto
  ): Promise<{ message: string }> {
    const response = await fetch(`${API_URL}/organizations/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete organization');
    }

    return response.json();
  },

  async restoreOrganization(token: string, id: string): Promise<Organization> {
    const response = await fetch(`${API_URL}/organizations/${id}/restore`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to restore organization');
    }

    return response.json();
  },
};
